import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { LoaderIcon } from 'lucide-react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
  {
    variants: {
      variant: {
        fill: 'bg-grey-800 text-white hover:bg-mint-400 hover:text-grey-800 active:bg-grey-800 active:text-white disabled:bg-grey-100 disabled:text-grey-300',
        outline:
          'border border-grey-300 text-grey-800 bg-white hover:bg-mint-50 hover:border-mint-400 hover:text-mint-700 active:bg-mint-100 active:text-mint-900 disabled:border-grey-200 disabled:text-grey-300 disabled:bg-grey-100',
        ghost:
          'text-grey-800 bg-transparent hover:bg-mint-50 hover:text-mint-700 active:bg-mint-100 active:text-mint-900 disabled:text-grey-300',
        link: 'text-grey-800 underline-offset-4 hover:underline hover:text-mint-700 active:text-mint-900 focus-visible:ring-mint-400 disabled:text-grey-400',
        destructive:
          'bg-red-400 text-white hover:bg-red-500 active:bg-red-500 focus-visible:ring-red-400 disabled:bg-grey-200 disabled:text-grey-400',
        active:
          'border border-grey-100 text-grey-800 bg-mint-200 hover:bg-mint-50 hover:border-mint-400 hover:text-mint-700 active:bg-mint-100 active:text-mint-900 disabled:border-grey-200 disabled:text-grey-300 disabled:bg-grey-100',
      },
      size: {
        sm: 'h-6 rounded-md px-2 text-xs',
        md: 'h-9 rounded-md px-4 text-sm',
        lg: 'h-12 rounded-md px-8 text-base',
        icon: 'h-9 w-9 rounded-md p-0',
      },
      iconPosition: {
        left: '',
        right: '',
        none: '',
      },
    },
    defaultVariants: {
      variant: 'fill',
      size: 'md',
      iconPosition: 'none',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isLoading,
      disabled,
      asChild = false,
      children,
      leftIcon,
      rightIcon,
      iconPosition,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, iconPosition, className }), {
          'disabled:opacity-100': isLoading,
        })}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <div className="relative w-full">
            <span className="invisible">{children}</span>
            <span className="absolute inset-0 flex items-center justify-center">
              <LoaderIcon className="h-4 w-4 animate-spin" />
            </span>
          </div>
        ) : (
          <>
            {leftIcon && <span className="mr-2 flex items-center">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="ml-2 flex items-center">{rightIcon}</span>}
          </>
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
